import { useSelector } from 'react-redux';
import { selectClient } from '../model';
import {
  carePhaseDefs,
  formatCurrency,
  formatInteger,
  formatThousands,
  formatPercent,
  fundingPolicyTypeDefs,
  isNullOrUndefined,
  combineSortClientPartnerPhaseCosts,
  isJointPolicy,
  checkPolicyCalculationTypeEquals,
  careEnvironmentDefs,
} from '@shared';
import { possessivePronoun } from '../util';
import { CareEnvironment, CarePhase, FundingPolicyType, hasFlatDeathBenefit } from '@shared';

import React, { createContext, useContext } from 'react';
import { calcLtcEndYear } from '@shared';
import { useFeatureFlags } from '@client/contexts/FeatureFlagsContext';

const WhiteBoxContext = createContext<
  | {
      ltcPolicy: PolicyFundingSource;
      phaseCalculations: SinglePhaseCalculations[];
    }
  | undefined
>(undefined);

type LtcPolicyProviderProps = {
  children: React.ReactNode;
  ltcPolicy: PolicyFundingSource;
  phaseCalculations: SinglePhaseCalculations[];
};

const WhiteBoxContextProvider = ({
  children,
  ltcPolicy,
  phaseCalculations,
}: LtcPolicyProviderProps) => {
  return (
    <WhiteBoxContext.Provider value={{ ltcPolicy, phaseCalculations }}>
      {children}
    </WhiteBoxContext.Provider>
  );
};

const useWhiteBoxContext = () => {
  const context = useContext(WhiteBoxContext);
  if (context === undefined) {
    throw new Error('context must be used within a WhiteBoxContextProvider');
  }
  return context;
};

type InsuranceWhiteBoxSlideOutContentProps = {
  fundingSourceId: string;
};

function boldCurrency(amount: number | null | undefined): string {
  return `<b>${formatCurrencyTwoDecimals(amount)}</b>`;
}

function boldUnderlineCurrency(amount: number | null | undefined): string {
  return `<u><b>${formatCurrencyTwoDecimals(amount)}</b></u>`;
}

function boldInteger(amount: number | null | undefined): string {
  return `<b>${formatInteger(amount ?? 0)}</b>`;
}

function boldThousands(amount: number | null | undefined): string {
  return `<b>${formatThousands(amount ?? 0, '0', 1)}</b>`;
}

function boldUnderlineInteger(amount: number | null | undefined): string {
  return `<b><u>${formatInteger(amount ?? 0)}</u></b>`;
}

export function InsuranceWhiteBoxSlideOutContent({
  fundingSourceId,
}: InsuranceWhiteBoxSlideOutContentProps) {
  const { flags } = useFeatureFlags();
  const client = useSelector(selectClient);
  const {
    clientFirstName,
    intakeSurvey: { clientHasStartedLtc },
    intakeSurvey,
    appliedInferenceSet: { ltcAtYear },
    allPhaseCosts: { allPhaseCareMonthsNeeded },
    policyPhaseCalculations,
    multipleFundingSources,
  } = client;

  const ltcPolicy = multipleFundingSources.ltcPolicy[fundingSourceId];
  const phaseCalculations = policyPhaseCalculations[fundingSourceId];
  const {
    policyLumpSumPayment,
    policyPremiumMonthlyCost,
    policyType,
    policyLimitedPayYears,
    policyPremiumStartYear,
    premiumInvestmentPeriodMonths,
    monthlyPremiumTotalCost,
    policyPremiumIncreaseRate,
    policyTotalCost,
    projectedCostCoverage,
    policyBenefitPeriodMonths,
    policyCoverageOfRemainingLtcCosts,
    policyPremiumIncreaseYears,
    indemnitySurplus: totalIndemnitySurplus,
  } = ltcPolicy;

  const getTotalInvestedIntoPolicyBody = () => {
    let totalInvestedIntoPolicyBody: string = '';
    const policyTypeDescription = flags.nycLifeDemo
      ? 'Insurance'
      : policyType
        ? fundingPolicyTypeDefs[policyType].label
        : 'Insurance';

    // Only lump-sum
    if (policyLumpSumPayment && !policyPremiumMonthlyCost) {
      totalInvestedIntoPolicyBody = `Total investment into the <b>${policyTypeDescription}</b> is the single lump sum payment: ${boldUnderlineCurrency(policyLumpSumPayment)}.<br/>`;
    }

    const nonNegativePremiumInvestmentMonths = Math.max(0, premiumInvestmentPeriodMonths ?? 0);

    if (policyPremiumMonthlyCost) {
      if (policyPremiumStartYear && premiumInvestmentPeriodMonths) {
        totalInvestedIntoPolicyBody += `Monthly premiums: ${boldCurrency(policyPremiumMonthlyCost)}.<br/>`;

        const investmentPeriodExplanation =
          policyLimitedPayYears && policyLimitedPayYears < nonNegativePremiumInvestmentMonths
            ? `Months in limited pay period (<b>${policyLimitedPayYears} years</b>): <b>${nonNegativePremiumInvestmentMonths} months.</b><br/>`
            : policyType === FundingPolicyType.fortifiedLife
              ? `Months between policy purchase and predicted care end year: <b>${formatThousands(nonNegativePremiumInvestmentMonths, '', 2)} months.</b> (${policyPremiumStartYear}-${formatInteger(calcLtcEndYear(client))}).<br/>`
              : `Months between policy purchase and ${clientHasStartedLtc ? 'assumed' : 'predicted'} activation: <b>${formatThousands(nonNegativePremiumInvestmentMonths, '', 2)} months.</b> (${policyPremiumStartYear}-${ltcAtYear}).<br/>`;

        totalInvestedIntoPolicyBody += investmentPeriodExplanation;

        // Updated premium calculation explanation to account for increase rate and years
        if (policyPremiumIncreaseRate && policyPremiumIncreaseRate > 0) {
          const increaseYears =
            policyPremiumIncreaseYears && policyPremiumIncreaseYears > 0
              ? Math.min(policyPremiumIncreaseYears, nonNegativePremiumInvestmentMonths / 12)
              : nonNegativePremiumInvestmentMonths / 12;

          const totalYears = nonNegativePremiumInvestmentMonths / 12;
          const remainingYears = totalYears - increaseYears;

          // Calculate the final monthly premium after all increases
          const finalMonthlyPremium =
            policyPremiumMonthlyCost * Math.pow(1 + policyPremiumIncreaseRate, increaseYears);

          const increaseExplanation =
            remainingYears > 0
              ? ` for the first <b>${formatThousands(increaseYears, '', 2)}</b> years, reaching a final monthly premium of ${boldCurrency(finalMonthlyPremium)}, then continuing at this rate for <b>${formatThousands(remainingYears, '', 2)}</b> additional years`
              : ` over <b>${formatThousands(increaseYears, '', 2)}</b> years, reaching a final monthly premium of ${boldCurrency(finalMonthlyPremium)}`;

          totalInvestedIntoPolicyBody += `Predicted total monthly premiums: Initial monthly premium ${boldCurrency(policyPremiumMonthlyCost)} with annual increase of <b>${formatPercent(policyPremiumIncreaseRate, 2)}</b>${increaseExplanation} = ${boldCurrency(monthlyPremiumTotalCost)}.<br/>`;
        } else {
          totalInvestedIntoPolicyBody += `Predicted total monthly premiums: <b>${nonNegativePremiumInvestmentMonths}</b> months x ${boldCurrency(policyPremiumMonthlyCost)} = ${boldCurrency(monthlyPremiumTotalCost)}.<br/>`;
        }
      }

      if (policyLumpSumPayment) {
        totalInvestedIntoPolicyBody += `Lump-sum payment: ${boldCurrency(policyLumpSumPayment)}.<br/> `;
      }
    }

    if (policyLumpSumPayment && policyPremiumMonthlyCost) {
      totalInvestedIntoPolicyBody += `Total monthly premiums plus lump-sum: ${boldCurrency(monthlyPremiumTotalCost)} + ${boldCurrency(policyLumpSumPayment)} = ${boldUnderlineCurrency(policyTotalCost)}.`;
    }

    return (
      <div>
        <p
          className="pb-1"
          dangerouslySetInnerHTML={{ __html: totalInvestedIntoPolicyBody }}
        ></p>
      </div>
    );
  };

  const getPayoutOfPolicyBody = () => {
    const policyTypeDescription = policyType
      ? fundingPolicyTypeDefs[policyType].label
      : 'Insurance';
    const totalPolicyCoverage = clientHasStartedLtc
      ? policyCoverageOfRemainingLtcCosts
      : projectedCostCoverage;

    let shortTermCareBenefitPeriodSubtitle = `Short-term care benefit period`;
    let shortTermCareBenefitPeriodBody = `Short Term Care policies have a benefit period, which is the maximum amount of time that the policy will make payments towards eligible long-term care costs.
       ${clientFirstName}'s Short Term Care policy has a maximum benefit period of <b>${policyBenefitPeriodMonths} months${clientHasStartedLtc ? ' remaining' : ''}</b>.`;
    if (policyBenefitPeriodMonths && allPhaseCareMonthsNeeded > policyBenefitPeriodMonths) {
      shortTermCareBenefitPeriodBody += ` By narrowing our calculations to only cover the first <b>${policyBenefitPeriodMonths} months</b> of ${possessivePronoun(intakeSurvey)} ${
        clientHasStartedLtc ? 'remaining ' : ''
      }care, we find that the total expected payout would be <b>${formatCurrencyTwoDecimals(totalPolicyCoverage)}</b>.`;
    } else {
      shortTermCareBenefitPeriodBody += ` Since this maximum benefit period is greater than ${clientFirstName}'s predicted ${clientHasStartedLtc ? 'remaining ' : ''}long-term care, we would not expect it to restrict the policy's payout, so the total expected payout would remain as <b>${formatCurrencyTwoDecimals(totalPolicyCoverage)}</b>.`;
    }

    return (
      <>
        <CarePhasesPayout
          client={client}
          policyTypeDescription={policyTypeDescription}
        />
        <LtcPayoutSummary
          client={client}
          policyTypeDescription={policyTypeDescription}
        />

        {policyBenefitPeriodMonths ? (
          <>
            <h3 className="flex flex-grow flex-col gap-3 pb-2 text-lg font-semibold">
              {shortTermCareBenefitPeriodSubtitle}
            </h3>
            <p
              className="pb-1"
              dangerouslySetInnerHTML={{
                __html: shortTermCareBenefitPeriodBody,
              }}
            ></p>
          </>
        ) : null}
        <IndemnitySurplusSection
          client={client}
          totalIndemnitySurplus={totalIndemnitySurplus ?? null}
        />
        {checkPolicyCalculationTypeEquals(policyType, [
          FundingPolicyType.hybridLifeInsurance,
          FundingPolicyType.lifeInsuranceWithRider,
          FundingPolicyType.hybridLifeIra,
        ]) ? (
          <DeathBenefitSection
            client={client}
            ltcPolicy={ltcPolicy}
          />
        ) : null}
      </>
    );
  };

  return (
    <WhiteBoxContextProvider
      ltcPolicy={ltcPolicy}
      phaseCalculations={phaseCalculations}
    >
      <div className="-mt-6 flex flex-grow flex-col gap-4 ">
        <div className="rounded-lg bg-blue-50 p-4">
          <p className="text-xs">
            <span className="font-semibold">Disclaimer:</span> The coverage details below assume
            this policy is used as if it is the only coverage in place. If multiple policies are
            shown, each is modeled independently and not combined together. While you may use this
            policy alongside other coverages in practice, Waterlily can only model combined coverage
            with self-funding or annuity options.
          </p>
        </div>
        <CoverageExclusionsDisclosure
          ltcPolicy={ltcPolicy}
          bodyText="Not all care-related costs during long-term care are covered by
        Fortified Life. For a detailed list of covered services, please refer to
        the Continuing Care Agreement."
        />
        <FortifiedLifeSummary
          ltcPolicy={ltcPolicy}
          phaseCalculations={phaseCalculations}
        />
        <div>
          <h2 className="flex flex-grow flex-col gap-3 pb-2 text-xl font-semibold">
            Total Invested Into Policy
          </h2>
          {getTotalInvestedIntoPolicyBody()}
        </div>
        <div>
          <h2 className="flex flex-grow flex-col gap-3 pb-2 text-xl font-semibold">
            Calculating the Policy Payout
          </h2>
          {getPayoutOfPolicyBody()}
        </div>
      </div>
    </WhiteBoxContextProvider>
  );
}

type CoverageExclusionsDisclosureProps = {
  ltcPolicy: PolicyFundingSource;
  bodyText: string;
};

function CoverageExclusionsDisclosure({ ltcPolicy, bodyText }: CoverageExclusionsDisclosureProps) {
  const { policyType } = ltcPolicy;
  if (policyType !== FundingPolicyType.fortifiedLife) {
    return null;
  }
  return (
    <div className="flex flex-grow flex-col gap-3">
      <h2 className="flex flex-grow flex-col text-xl font-semibold">Coverage Exclusions</h2>
      <p>{bodyText}</p>
    </div>
  );
}

function FortifiedLifeSummary({
  ltcPolicy,
  phaseCalculations,
}: {
  ltcPolicy: PolicyFundingSource;
  phaseCalculations: SinglePhaseCalculations[];
}) {
  const { policyType, policyTotalCost, policyCoverageOfRemainingLtcCosts, projectedCostCoverage } =
    ltcPolicy;
  const {
    phaseCosts,
    allPhaseCosts: { allPhaseCareMonthsNeeded },
    intakeSurvey: { clientHasStartedLtc },
  } = useSelector(selectClient);

  if (policyType !== FundingPolicyType.fortifiedLife) {
    return null;
  }
  const totalPolicyCoverage = clientHasStartedLtc
    ? policyCoverageOfRemainingLtcCosts
    : projectedCostCoverage;

  const breakEvenMonths = calculateBreakEvenMonths(
    phaseCalculations.map((phaseCalc, index) => ({
      phaseMonthlyCost: phaseCalc.phaseMonthlyCost,
      phaseCareMonthsNeeded: phaseCosts[index].phaseCareMonthsNeeded,
    })),
    policyTotalCost ?? 0,
  );

  return (
    <div className="flex flex-grow flex-col gap-1">
      <h2 className="flex flex-grow flex-col pb-2 text-xl font-semibold">Fortified Life Summary</h2>
      <p>
        Total invested into Fortified Life:
        <span className="font-semibold">{` ${formatCurrency(policyTotalCost)}`}</span>
      </p>
      <p>
        Projected cost of care (without Fortified Life):
        <span className="font-semibold">{` ${formatCurrency(totalPolicyCoverage)}`}</span>
      </p>

      {breakEvenMonths && (
        <>
          <p>
            Estimated break-even point:
            <span className="font-semibold">
              {` ${formatInteger(breakEvenMonths)} months of care`}
            </span>
          </p>
          <p>
            Projected cost savings with Fortified Life:
            <span className="font-semibold">
              {` ${formatCurrency(
                Math.max((totalPolicyCoverage ?? 0) - (policyTotalCost ?? 0), 0),
              )} (${formatInteger(allPhaseCareMonthsNeeded - breakEvenMonths)} months of care)`}
            </span>
          </p>
        </>
      )}
    </div>
  );
}

type PhaseMonthlyCosts = {
  phaseMonthlyCost: number;
  phaseCareMonthsNeeded: number;
};

function calculateBreakEvenMonths(
  phaseMonthlyCosts: PhaseMonthlyCosts[],
  policyTotalCost: number,
): number | undefined {
  let cumulativeCost = 0;
  let totalMonths = 0;

  for (const phase of phaseMonthlyCosts) {
    const { phaseMonthlyCost, phaseCareMonthsNeeded } = phase;

    // Calculate the maximum cost this phase can contribute
    for (let i = 0; i < phaseCareMonthsNeeded; i++) {
      cumulativeCost += phaseMonthlyCost;
      totalMonths++;

      // Check if we have reached or exceeded the policyTotalCost
      if (cumulativeCost >= policyTotalCost) {
        return totalMonths; // Break even point reached
      }
    }
  }

  return undefined;
}

function DeathBenefitSection({
  client,
  ltcPolicy,
}: {
  client: Client;
  ltcPolicy: PolicyFundingSource;
}) {
  const {
    intakeSurvey: { clientHasStartedLtc },
  } = client;
  const {
    policyInflatedDeathBenefitAmount,
    policyCoverageOfRemainingLtcCosts,
    policyMinimumGuaranteedBenefitAmount,
    indemnitySurplus: totalIndemnitySurplus,
    projectedCostCoverage,
    policyMaximumBenefitAmount,
    policyType,
  } = ltcPolicy;

  const appliedDeathBenefit = hasFlatDeathBenefit(policyType)
    ? policyMaximumBenefitAmount
    : policyInflatedDeathBenefitAmount;

  const totalPolicyCoverage = clientHasStartedLtc
    ? policyCoverageOfRemainingLtcCosts
    : projectedCostCoverage;

  let deathBenefitSubtitle = `Death benefit`;
  let deathBenefitBody = `Initial death benefit amount (plus any inflation protection): ${boldCurrency(appliedDeathBenefit)}.<br/> `;
  const policyCoveragePlusIndemnitySurplus =
    (totalPolicyCoverage ?? 0) + (totalIndemnitySurplus ?? 0);
  const remainingDeathBenefit =
    appliedDeathBenefit && !isNullOrUndefined(policyCoveragePlusIndemnitySurplus)
      ? appliedDeathBenefit - policyCoveragePlusIndemnitySurplus
      : 0;

  deathBenefitBody += `Total policy payout: ${boldCurrency(policyCoveragePlusIndemnitySurplus)}.<br/>`;
  deathBenefitBody += `Remaining death benefit amount: ${boldCurrency(Math.max(remainingDeathBenefit, 0))}.<br/>`;
  deathBenefitBody += `Minimum guaranteed death benefit: ${boldCurrency(policyMinimumGuaranteedBenefitAmount ?? 0)}.<br/>`;

  let amountPaidToBeneficiaries = 0;
  let rationale = '';
  if (
    appliedDeathBenefit &&
    policyCoveragePlusIndemnitySurplus &&
    policyCoveragePlusIndemnitySurplus >= appliedDeathBenefit
  ) {
    amountPaidToBeneficiaries = policyMinimumGuaranteedBenefitAmount ?? 0;
    rationale += `Total policy payout is greater than or equal to the Death Benefit amount, so only pay out is the policy's minimum guaranteed death benefit.`;
    deathBenefitBody += `Total policy payout is greater than or equal to the Death Benefit amount, so only pay out is the policy's minimum guaranteed death benefit.`;
  } else {
    rationale += `Since the total policy payout is less than the Death Benefit amount, the amount that will be paid to beneficiaries is `;

    if (
      policyMinimumGuaranteedBenefitAmount &&
      policyMinimumGuaranteedBenefitAmount > remainingDeathBenefit
    ) {
      amountPaidToBeneficiaries = policyMinimumGuaranteedBenefitAmount;
      rationale += ` the policy's minimum guaranteed death benefit, since this amount is greater than the remaining Death Benefit amount `;
    } else {
      amountPaidToBeneficiaries = remainingDeathBenefit;
      rationale += ` the remaining Death Benefit amount, since this amount is greater than the policy's minimum guaranteed death benefit. `;
    }
    deathBenefitBody += `Expected death benefit payout: ${boldUnderlineCurrency(amountPaidToBeneficiaries)} (added to the <b>Non-LTC Payout</b>).<br/>`;
    deathBenefitBody += `<i>${rationale}</i>`;
  }

  return (
    <>
      <h3 className="flex flex-grow flex-col gap-3 pb-2 pt-3 text-lg font-semibold">
        {deathBenefitSubtitle}
      </h3>
      <p
        className="pb-1"
        dangerouslySetInnerHTML={{
          __html: deathBenefitBody,
        }}
      ></p>
    </>
  );
}

type IndemnitySurplusProps = {
  client: Client;
  totalIndemnitySurplus: number | null;
};

function IndemnitySurplusSection({ client, totalIndemnitySurplus }: IndemnitySurplusProps) {
  if (!totalIndemnitySurplus) {
    return null;
  }
  let indemnityTotalSurplusSubtitle = `Indemnity Policy Surplus Funds`;
  let indemnityTotalSurplusBody = `Indemnity surplus after Full Care Phase: ${boldUnderlineCurrency(totalIndemnitySurplus)}.<br/>
   These are funds paid out by the policy that were not used to to cover projected LTC costs. This amount is added to the <b>Non-LTC Payout total</b>.`;
  return (
    <>
      <h3 className="mt-2 flex flex-grow flex-col gap-3 pb-2 text-lg font-semibold">
        {indemnityTotalSurplusSubtitle}
      </h3>
      <p
        className="pb-1"
        dangerouslySetInnerHTML={{
          __html: indemnityTotalSurplusBody,
        }}
      ></p>
    </>
  );
}

interface CarePhasePayoutProps {
  client: Client;
  policyTypeDescription: string;
}

function LtcPayoutSummary({ client }: CarePhasePayoutProps) {
  const { clientFirstName } = client;

  const { ltcPolicy, phaseCalculations } = useWhiteBoxContext();
  const { policyCoverageOfRemainingLtcCosts, projectedCostCoverage, policyType } = ltcPolicy;

  const jointPolicy = isJointPolicy(policyType, client.mutableClientPartner);

  let totalPolicyPayoutSubtitle = `Long-Term Care Payout Summary`;
  let totalPolicyPayoutBody = `The long-term care payout is the total projected coverage all care phases for ${clientFirstName} ${jointPolicy ? `and ${client.mutableClientPartner?.clientFirstName}` : ''}. Based on our calculations, the policy terms indicate that this policy is expected to pay out:`;

  if (jointPolicy) {
    const combinedClientPhaseCosts = combineSortClientPartnerPhaseCosts(client);

    let phasePayoutSummary = combinedClientPhaseCosts.map(clientPhaseCosts => {
      const currentClient = clientPhaseCosts.client;
      const carePhase = clientPhaseCosts.phaseCosts.carePhase;
      const carePhaseIndex = carePhaseDefs[carePhase].index;
      const isClientPartner = currentClient.clientId !== client.clientId;
      const singlePhaseCalculations = isClientPartner
        ? client.mutableClientPartner!.phaseCalculations[carePhaseIndex]
        : phaseCalculations[carePhaseIndex];

      if (isNullOrUndefined(singlePhaseCalculations.costCoverageAddedToTotal)) {
        return null;
      }
      return ` <br/><b>${formatCurrencyTwoDecimals(singlePhaseCalculations.costCoverageAddedToTotal)}</b> for ${currentClient.clientFirstName}'s ${carePhaseDefs[carePhase].label} phase${clientPhaseCosts.phaseStartYear ? ` (expected start year ${Math.round(clientPhaseCosts.phaseStartYear)})` : ''}.`;
    });
    totalPolicyPayoutBody += phasePayoutSummary.join('');
  } else {
    let phasePayoutSummary = phaseCalculations
      // .filter(phaseCalc => phaseCalc.costCoverageAddedToTotal)
      .map(
        phaseCalc =>
          ` <br/><b>${formatCurrencyTwoDecimals(phaseCalc.costCoverageAddedToTotal)}</b> in the ${carePhaseDefs[phaseCalc.phase].label} phase`,
      );

    if (phasePayoutSummary.length > 1) {
      const lastDetail = phasePayoutSummary.pop();
      totalPolicyPayoutBody += ` ${phasePayoutSummary.join('')} ${lastDetail}`;
    } else if (phasePayoutSummary.length === 1) {
      totalPolicyPayoutBody += ` ${phasePayoutSummary[0]}.`;
    }
  }

  const policyCoverage = projectedCostCoverage
    ? projectedCostCoverage
    : policyCoverageOfRemainingLtcCosts;

  let totalPolicyCoverage;

  if (jointPolicy) {
    const partnerProjectedCostCoverage =
      client.mutableClientPartner?.fundingSources.ltcPolicy.projectedCostCoverage;
    const partnerPolicyCoverageOfRemainingLtcCosts =
      client.mutableClientPartner?.fundingSources.ltcPolicy.policyCoverageOfRemainingLtcCosts;
    const partnerPolicyCoverage =
      (partnerProjectedCostCoverage
        ? partnerProjectedCostCoverage
        : partnerPolicyCoverageOfRemainingLtcCosts) ?? 0;

    totalPolicyCoverage = (policyCoverage ?? 0) + (partnerPolicyCoverage ?? 0);
  } else {
    totalPolicyCoverage = policyCoverage ?? 0;
  }

  totalPolicyPayoutBody += ` <br/><b>${formatCurrencyTwoDecimals(totalPolicyCoverage)}</b> total policy coverage.`;
  return (
    <>
      <h3 className="flex flex-grow flex-col gap-3 pb-2 pt-3 text-lg font-semibold">
        {totalPolicyPayoutSubtitle}
      </h3>
      <p
        className="pb-1"
        dangerouslySetInnerHTML={{
          __html: totalPolicyPayoutBody,
        }}
      ></p>
    </>
  );
}

function CarePhasesPayout({ client }: CarePhasePayoutProps) {
  const {
    clientFirstName,
    phaseCosts,
    intakeSurvey: { clientCurrentCarePhase },
    clientId,
  } = client;

  const { ltcPolicy, phaseCalculations } = useWhiteBoxContext();
  const { policyType } = ltcPolicy;

  if (isJointPolicy(policyType, client.mutableClientPartner)) {
    const combinedClientPhaseCosts = combineSortClientPartnerPhaseCosts(client);
    return (
      <>
        <span className="py-1">
          Since this is a <span className="font-semibold">joint policy </span>
          the below section will explain the payouts for both {clientFirstName}
          's and {client.mutableClientPartner.clientFirstName}'s care phases.{' '}
        </span>

        {combinedClientPhaseCosts.map((clientPhaseCosts, keyIndex) => {
          const carePhase = clientPhaseCosts.phaseCosts.carePhase;
          const carePhaseIndex = carePhaseDefs[carePhase].index;

          const singlePhaseCalculations =
            clientPhaseCosts.client.clientId === clientId
              ? phaseCalculations[carePhaseIndex]
              : clientPhaseCosts.client.phaseCalculations[carePhaseIndex];

          const currentCarePhase = clientPhaseCosts.client.intakeSurvey.clientCurrentCarePhase;

          const isPastPhase = currentCarePhase
            ? carePhaseDefs[currentCarePhase].index > carePhaseDefs[carePhase].index
            : null;

          if (isPastPhase) {
            return null;
          }

          return (
            <CarePhasePayoutSections
              client={clientPhaseCosts.client}
              phaseCosts={clientPhaseCosts.phaseCosts}
              phaseCalculations={singlePhaseCalculations}
              isJointPolicy={true}
            />
          );
        })}
      </>
    );
  } else {
    return (
      <>
        {phaseCosts.map((singlePhaseCosts, keyIndex) => {
          const carePhase = singlePhaseCosts.carePhase;
          const carePhaseIndex = carePhaseDefs[carePhase].index;
          const singlePhaseCalculations = phaseCalculations[carePhaseIndex];
          const isPastPhase = clientCurrentCarePhase
            ? carePhaseDefs[clientCurrentCarePhase].index > carePhaseDefs[carePhase].index
            : null;

          if (isPastPhase) {
            return null;
          }

          return (
            <CarePhasePayoutSections
              client={client}
              phaseCosts={singlePhaseCosts}
              phaseCalculations={singlePhaseCalculations}
            />
          );
        })}
      </>
    );
  }
}

function getCareEnvironmentDescription(client: Client, carePhase: CarePhase): string {
  const careLocation = client.appliedCareEnvironments?.[carePhase] as CareEnvironment;
  let careLocationDescription;

  switch (careLocation) {
    case CareEnvironment.home:
      careLocationDescription = 'at home';
      break;
    case CareEnvironment.assistedLiving:
      careLocationDescription = 'at an assisted living facility';
      break;
    case CareEnvironment.fullCareFacility:
      careLocationDescription = 'at a full-care facility';
      break;
    case CareEnvironment.independentLiving:
      careLocationDescription = 'at an independent living facility';
      break;
    default:
      throw new Error('Invalid care location');
  }
  return careLocationDescription;
}

interface ClientPhaseProps {
  client: Client;
  phaseCosts: SinglePhaseCosts;
  phaseCalculations: SinglePhaseCalculations;
  isJointPolicy?: boolean;
}

function CarePhasePayoutSections({
  client,
  phaseCosts,
  phaseCalculations,
  isJointPolicy,
}: ClientPhaseProps): JSX.Element {
  const phaseComponents = [
    PhaseCostBreakdown,
    PhaseMaxBenefitRemaining,
    PolicyEligibleCareDays,
    PolicyDailyMaximumBenefit,
    PhaseCostCoverageAndRemainingBenefit,
    IndemnitySurplus,
  ];
  return (
    <div>
      <div className="ml-2">
        {phaseComponents.map((Component, index) => (
          <Component
            key={index}
            isJointPolicy={isJointPolicy}
            client={client}
            phaseCosts={phaseCosts}
            phaseCalculations={phaseCalculations}
          />
        ))}
      </div>
    </div>
  );
}

function PhaseMaxBenefitRemaining({ client, phaseCosts, phaseCalculations }: ClientPhaseProps) {
  const { clientFirstName, clientPhasePredictedEndYears } = client;

  const { ltcPolicy } = useWhiteBoxContext();
  const {
    policyMaximumBenefitAmount,
    policyType,
    policyInflationProtection,
    policyInflationProtectionOnCOBAmount,
    policyInflationProtectionOnFaceAmount,
    policyBenefitUtilizedToDate,
    isSimpleInflationProtection,
    policyContinuationBenefitAmount,
    policyBenefitIsUnlimited,
    policyPremiumStartYear,
    policyInflationProtectionYears,
    policySharedBenefitPool,
  } = ltcPolicy;
  const {
    inflationPeriodYears,
    inflatedMaxBenefitAmount,
    remainingMaxBenefitBeforePhaseReduction,
    cumulativePolicyPayOut,
    policySharedBenefitPoolRemaining,
  } = phaseCalculations;

  const maxBenefitSubtitle = 'Policy benefit available for this phase';

  const predictedPhaseEndYear = clientPhasePredictedEndYears[phaseCosts.carePhase];

  let maxBenefitBody = '';
  let inflationProtectionPeriodText = policyInflationProtectionYears
    ? `Inflation protection period: ${boldThousands(policyInflationProtectionYears)} years<br/>`
    : '';
  let inflationProtectionText = '';
  if (
    policyInflationProtection ||
    policyInflationProtectionOnFaceAmount ||
    policyInflationProtectionOnCOBAmount
  ) {
    inflationProtectionText +=
      !policyBenefitIsUnlimited && inflatedMaxBenefitAmount
        ? `Inflation protection: <b>${formatPercent(policyInflationProtection)}</b> ${isSimpleInflationProtection ? 'simple' : 'compound'}.<br/>
    Inflated benefit amount: ${boldCurrency(inflatedMaxBenefitAmount)}.<br/>`
        : '';
  }
  if (policyInflationProtectionOnFaceAmount && policyInflationProtectionOnFaceAmount > 0) {
    inflationProtectionText += `Face Amount inflation protection: <b>${formatPercent(policyInflationProtectionOnFaceAmount)}</b> compound.<br/>`;
  }

  if (
    policyInflationProtectionOnCOBAmount &&
    policyInflationProtectionOnCOBAmount > 0 &&
    policyContinuationBenefitAmount
  ) {
    inflationProtectionText += `Continuation of Benefits inflation protection: <b>${formatPercent(policyInflationProtectionOnCOBAmount)}</b> compound.<br/>
    `;
  }

  if (
    policyInflationProtection ||
    policyInflationProtectionOnFaceAmount ||
    policyInflationProtectionOnCOBAmount
  ) {
    inflationProtectionText += inflationProtectionPeriodText;
    inflationProtectionText += `<i>Inflation rate(s) is applied to the policy's benefit pool(s) from the year the policy was purchased ${boldInteger(policyPremiumStartYear)} to the year ${clientFirstName}'s ${carePhaseDefs[phaseCosts.carePhase].label} is expected to end ${formatInteger(predictedPhaseEndYear)}${policyInflationProtectionYears ? `, or for the policy's inflation protection period of ${formatInteger(policyInflationProtectionYears)} years, whichever is less` : ''}, for a total of ${formatThousands(inflationPeriodYears, '0', 2)} years.</i><br/>`;
  }

  const benefitsUtilizedToDateText = policyBenefitUtilizedToDate
    ? `Benefit amount utilized: ${boldCurrency(policyBenefitUtilizedToDate)}.<br/>`
    : '';

  const policyPayOutText = `Amount paid out in previous phases: ${boldCurrency(cumulativePolicyPayOut)}.<br/>`;

  // unlimited benefit
  if (policyBenefitIsUnlimited) {
    maxBenefitBody += `The LTC benefit is <b>unlimited</b> for this policy. <br/>`;
  }
  // shared care plan (shared separate benefit pool)
  if (
    policyType === FundingPolicyType.jointNglEssentialLtc ||
    policyType === FundingPolicyType.nylJointSecureCare
  ) {
    maxBenefitBody += `Initial individual pool amount: ${boldCurrency(policySharedBenefitPool)}.<br/>`;
    maxBenefitBody += inflatedMaxBenefitAmount > 0 ? inflationProtectionText : '';
    maxBenefitBody += policyPayOutText;

    maxBenefitBody += `Remaining individual pool amount: ${boldCurrency(
      Math.max(
        remainingMaxBenefitBeforePhaseReduction - (policySharedBenefitPoolRemaining ?? 0),
        0,
      ),
    )}.<br/>`;
    maxBenefitBody += `Remaining shared benefit pool: ${boldCurrency(
      policySharedBenefitPoolRemaining ?? 0,
    )}.<br/>`;
  } else {
    // non-shared care plan
    if (
      checkPolicyCalculationTypeEquals(policyType, [
        FundingPolicyType.hybridLifeInsurance,
        FundingPolicyType.hybridLifeIra,
      ]) &&
      policyContinuationBenefitAmount
    ) {
      maxBenefitBody += `Death benefit amount: ${boldCurrency(policyMaximumBenefitAmount)}.<br/>
        Continuation of benefits amount: ${boldCurrency(policyContinuationBenefitAmount)}.<br/>`;
    } else if (
      checkPolicyCalculationTypeEquals(policyType, FundingPolicyType.lifeInsuranceWithRider)
    ) {
      maxBenefitBody += `Initial death benefit amount: ${boldCurrency(policyMaximumBenefitAmount)}.<br/> `;
    } else {
      maxBenefitBody += `Initial max benefit amount: ${boldCurrency(policyMaximumBenefitAmount)}.<br/> `;
    }

    maxBenefitBody += inflatedMaxBenefitAmount > 0 ? inflationProtectionText : '';
    maxBenefitBody += benefitsUtilizedToDateText;
    maxBenefitBody += policyPayOutText;
  }

  if (!policyBenefitIsUnlimited) {
    maxBenefitBody += `Policy benefit available/remaining for phase: ${boldUnderlineCurrency(remainingMaxBenefitBeforePhaseReduction)}.<br/>`;
  }
  return (
    <>
      <h3 className="flex flex-grow flex-col gap-3 pb-2 text-base font-semibold">
        {maxBenefitSubtitle}
      </h3>
      <p
        className="pb-1"
        dangerouslySetInnerHTML={{
          __html: maxBenefitBody,
        }}
      ></p>
    </>
  );
}

function PhaseCostCoverageAndRemainingBenefit({
  client,
  phaseCosts,
  phaseCalculations,
}: ClientPhaseProps): JSX.Element | null {
  const { ltcPolicy } = useWhiteBoxContext();
  const { isIndemnityPolicyPayment, policyBenefitIsUnlimited } = ltcPolicy;
  const {
    maxPayoutAccordingToDailyMaximum,
    portionOfPhaseCostBenefitEligible,
    phaseEligibleCareDaysAfterWaitingPeriod,
    surplusAppliedToCoverage,
    phaseCostPerDayNeeded,
    remainingMaxBenefitBeforePhaseReduction,
    costCoverageAddedToTotal,
    remainingMaxBenefitAfterPhaseReduction,
    indemnitySurplusBeforePhaseReduction,
    sharedPoolUtilized,
  } = phaseCalculations;

  const carePhase = phaseCosts.carePhase;
  const carePhaseLabel = carePhaseDefs[carePhase].label;

  let subtitle = policyBenefitIsUnlimited
    ? `Cost coverage for ${carePhaseDefs[carePhase].label} Phase`
    : `Cost coverage for ${carePhaseDefs[carePhase].label} Phase and remaining benefit`;

  let body = '';

  const eligibleCareDays = phaseEligibleCareDaysAfterWaitingPeriod;

  if (policyBenefitIsUnlimited) {
    body += `The total cost that is eligible for benefit payout is ${boldCurrency(portionOfPhaseCostBenefitEligible)}.<br/> `;
  } else {
    body += `Remaining benefit amount: ${boldCurrency(remainingMaxBenefitBeforePhaseReduction)}.<br/>`;
    body += `Eligible care cost: ${boldCurrency(portionOfPhaseCostBenefitEligible)}.<br/><i> This amount is calculated by multiplying eligible days by the average daily cost of care for this phase ${boldThousands(eligibleCareDays)} days x ${boldCurrency(phaseCostPerDayNeeded)}</i>.<br/>`;
  }

  if (maxPayoutAccordingToDailyMaximum) {
    body += `Max coverage based on daily maximum ${boldCurrency(maxPayoutAccordingToDailyMaximum)}.<br/>
    <i>See the Daily Maximum Benefit section for more details.</i><br/>`;
  }

  const indemnitySurplusAddedToCoverage = surplusAppliedToCoverage;

  if (indemnitySurplusAddedToCoverage) {
    body += `Indemnity surplus applied to coverage: ${boldCurrency(indemnitySurplusAddedToCoverage)}.<br/> `;

    if (
      indemnitySurplusBeforePhaseReduction &&
      indemnitySurplusBeforePhaseReduction > indemnitySurplusAddedToCoverage
    ) {
      // if the coverage gap is less than the indemnity surplus
      body += `<i>This amount is the lesser of the available indemnity surplus ${boldCurrency(indemnitySurplusBeforePhaseReduction)} and the coverage gap, which is the total cost of phase ${boldCurrency(phaseCosts.phaseInflatedProfessionalShareCost)} minus policy projected coverage ${boldCurrency(costCoverageAddedToTotal - indemnitySurplusAddedToCoverage)}.</i><br/>`;
    }
  }

  if (policyBenefitIsUnlimited) {
    body += `Total cost coverage: ${boldUnderlineCurrency(costCoverageAddedToTotal)}.<br/> `;
  } else {
    body += `Total cost coverage: ${boldUnderlineCurrency(costCoverageAddedToTotal)}.
    <br/>`;

    body += `Remaining benefit amount: ${boldCurrency(remainingMaxBenefitAfterPhaseReduction)} after reducing by the ${carePhaseLabel} payout.<br/>`;
    body += sharedPoolUtilized
      ? `Cumulative shared benefit pool utilized: ${boldCurrency(sharedPoolUtilized)}.<br/>`
      : '';
    body += `${isIndemnityPolicyPayment && maxPayoutAccordingToDailyMaximum > portionOfPhaseCostBenefitEligible ? `<i>Indemnity policies pay out the full daily max benefit, which in this case is more than the cost of care.</i>` : ``}`;
  }

  return (
    <>
      <h4 className="text-md flex flex-grow flex-col gap-3 pb-2 font-semibold">{subtitle}</h4>
      <p
        className="pb-1"
        dangerouslySetInnerHTML={{
          __html: body,
        }}
      ></p>
    </>
  );
}

function IndemnitySurplus({
  client,
  phaseCosts,
  phaseCalculations,
}: ClientPhaseProps): JSX.Element | null {
  const indemnityPayout = phaseCalculations.indemnityPayout;
  const phaseCostCoverage = phaseCalculations.phaseCostCoverage;
  let indemnitySurplusSubtitle = `Indemnity surplus for phase`;
  let indemnitySurplusBody = '';

  const indemnitySurplus = phaseCalculations.indemnitySurplusForPhase;

  if (indemnitySurplus) {
    indemnitySurplusBody = `Expected surplus: ${boldCurrency(indemnitySurplus)}. <br/><i>This amount is calculated by subtracting the amount paid by the policy by the predicted phase cost ${boldCurrency(indemnityPayout)} - ${boldCurrency(phaseCostCoverage)}. The surplus is applied to subsequent care phases where indemnity coverage falls short. If all phases are fully funded, any remaining surplus will be added to the Non-LTC Payout.</i><br/>

    `;
  }

  return (
    <>
      {indemnitySurplus ? (
        <>
          <h4 className="text-md flex flex-grow flex-col gap-3 pb-2 font-semibold">
            {indemnitySurplusSubtitle}
          </h4>
          <p
            className="pb-1"
            dangerouslySetInnerHTML={{
              __html: indemnitySurplusBody,
            }}
          ></p>
        </>
      ) : null}
    </>
  );
}

function PolicyDailyMaximumBenefit({
  client,
  phaseCosts,
  phaseCalculations,
}: ClientPhaseProps): JSX.Element | null {
  const {
    clientFirstName,
    clientPhasePredictedEndYears,
    appliedInferenceSet: { ltcAtAge },
  } = client;

  const { ltcPolicy } = useWhiteBoxContext();
  const {
    policyPremiumStartYear,
    isIndemnityPolicyPayment,
    isSimpleInflationProtection,
    policyType,
    policyMonthlyWithdrawalRate,
    policyInflationProtectionOnFaceAmount,
    policyInflationProtectionOnCOBAmount,
    policyInflationProtectionYears,
  } = ltcPolicy;

  const {
    baseAmountApplicableDays,
    riderAmountApplicableDays,
    dailyBenefitAmountBase,
    inflatedDailyBenefitAmount,
    inflatedDailyBenefitAmountBase,
    inflatedDailyBenefitAmountRider,
    inflatedFaceAmount,
    maxPayoutAccordingToDailyMaximum,
  } = phaseCalculations;

  const carePhase = phaseCosts.carePhase;

  const dailyMaximumBenefitForCareEnvironment = phaseCalculations.dailyBenefitAmount;
  const inflationProtection = phaseCalculations.inflationProtection;
  const inflationPeriodYears = phaseCalculations.inflationPeriodYears;
  const careLocationDescription = getCareEnvironmentDescription(client, carePhase);
  const policyPhaseEndYear = clientPhasePredictedEndYears[carePhase];
  const phaseCostPerDayNeeded = phaseCalculations.phaseCostPerDayNeeded;

  const inflatedDailyMaxText = `<i>Amount calculated by applying ${isSimpleInflationProtection ? 'simple' : 'compound'} inflation to the original daily benefit amount ${boldCurrency(dailyMaximumBenefitForCareEnvironment)} from the year the policy was purchased (<b>${policyPremiumStartYear}</b>) to the year the phase is expected to end (<b>${formatInteger(policyPhaseEndYear)}</b>)${policyInflationProtectionYears ? `, or for the policy's inflation protection period of ${formatInteger(policyInflationProtectionYears)} years, whichever is less` : ''}, for a total of <b>${formatThousands(inflationPeriodYears, '0', 2)} years</b></i><br/>`;

  const inflationProtectionText = `Inflation protection: <b>${formatPercent(inflationProtection)} ${isSimpleInflationProtection ? 'simple' : 'compound'}</b>.<br/>`;

  const dailyMaxExists = dailyMaximumBenefitForCareEnvironment > 0;
  let dailyMaximumBenefitSubtitle = `Daily maximum benefit`;
  let dailyMaximumBenefitBody = '';

  if (
    checkPolicyCalculationTypeEquals(policyType, FundingPolicyType.hybridLifeIra) &&
    dailyMaxExists
  ) {
    const cappedExplainerText = `<i>The daily max is capped by the average daily cost of care, since the policy will only pay up to the actual cost incurred.</i><br/>`;
    const cappedDailyBenefitAmountBase = Math.min(
      dailyBenefitAmountBase ?? 0,
      phaseCostPerDayNeeded,
    );
    const cappedDailyBenefitAmountRider = Math.min(
      inflatedDailyBenefitAmountRider ?? inflatedDailyBenefitAmount,
      phaseCostPerDayNeeded,
    );
    const maxCoverageFromBasePolicy =
      cappedDailyBenefitAmountBase * (baseAmountApplicableDays ?? 0);
    const maxCoverageFromRider = cappedDailyBenefitAmountRider * (riderAmountApplicableDays ?? 0);
    let baseDailyMaxText = `Daily max benefit (base): ${boldCurrency(cappedDailyBenefitAmountBase)}.<br/>`;
    baseDailyMaxText += `${cappedDailyBenefitAmountBase !== phaseCostPerDayNeeded ? `<i>The daily limit when withdrawing from the life insurance policy. Calculated by multiplying the projected death benefit at age ${ltcAtAge} (${boldCurrency(inflatedFaceAmount)}) by the monthly withdrawal rate (<b>${formatPercent(policyMonthlyWithdrawalRate)}</b>) and dividing by 30 days in a month. Unless the average daily cost is less than the daily max, the policy will only pay up to the actual cost incurred.</i><br/>` : ''}`;
    baseDailyMaxText += `${cappedDailyBenefitAmountBase === phaseCostPerDayNeeded ? cappedExplainerText : ''}`;
    baseDailyMaxText += `Max coverage from base policy: ${boldCurrency(maxCoverageFromBasePolicy)}.<br/>`;
    baseDailyMaxText += `<i>The policy withdraws from the death benefit until depleted. Amount calculated by multiplying the daily max (base) by the number of days the base amount is applicable ${boldCurrency(cappedDailyBenefitAmountBase)} x <b>${boldThousands(baseAmountApplicableDays)}</b> days</i><br/>`;

    let riderDailyMaxText = `Daily max benefit (rider): ${boldCurrency(cappedDailyBenefitAmountRider)}.<br/>`;
    riderDailyMaxText += `${policyInflationProtectionOnCOBAmount && cappedDailyBenefitAmountRider !== phaseCostPerDayNeeded ? inflatedDailyMaxText : ''}`;
    riderDailyMaxText += `${cappedDailyBenefitAmountRider === phaseCostPerDayNeeded ? cappedExplainerText : ''}`;
    riderDailyMaxText += `
      Max coverage from rider: ${boldCurrency(maxCoverageFromRider)}.<br/>
      <i>Since the death benefit is depleted, the rider monthly limit is in effect. Amount calculated by multiplying the daily max (rider) by the number of days the rider amount is applicable ${boldCurrency(cappedDailyBenefitAmountRider)} x <b>${boldThousands(riderAmountApplicableDays)}</b> days</i><br/>
      `;

    if (baseAmountApplicableDays && baseAmountApplicableDays > 0) {
      dailyMaximumBenefitBody += baseDailyMaxText;
    }

    if (riderAmountApplicableDays && riderAmountApplicableDays > 0) {
      dailyMaximumBenefitBody += riderDailyMaxText;
    }

    dailyMaximumBenefitBody += `Max coverage according to daily max: ${boldUnderlineCurrency(maxPayoutAccordingToDailyMaximum)}.<br/>`;
  } else if (
    checkPolicyCalculationTypeEquals(policyType, FundingPolicyType.hybridLifeInsurance) &&
    dailyMaxExists
  ) {
    const cappedExplainerText = `<i>The daily max is capped by the average daily cost of care, since the policy will only pay up to the actual cost incurred.</i><br/>`;

    // Add capping logic for base and rider amounts
    const cappedDailyBenefitAmountBase = Math.min(
      inflatedDailyBenefitAmountBase ?? 0,
      phaseCostPerDayNeeded,
    );
    const cappedDailyBenefitAmountRider = Math.min(
      inflatedDailyBenefitAmountRider ?? 0,
      phaseCostPerDayNeeded,
    );

    const inflatedDailyMaxFace = cappedDailyBenefitAmountBase * (baseAmountApplicableDays ?? 0);
    const inflatedDailyMaxCOB = cappedDailyBenefitAmountRider * (riderAmountApplicableDays ?? 0);

    // Add a summary section at the top
    dailyMaximumBenefitBody += `
      Original daily benefit: ${boldCurrency(dailyMaximumBenefitForCareEnvironment)}<br/>
      Total inflation period: <b>${formatThousands(inflationPeriodYears, '0', 2)} years</b><br/>
    `;

    if (baseAmountApplicableDays && baseAmountApplicableDays > 0) {
      dailyMaximumBenefitBody += `
        <u>Daily max during base/face amount:</u><br/>`;

      const inflationText = `
          Inflation rate: <b>${formatPercent(policyInflationProtectionOnFaceAmount ?? inflationProtection)} ${isSimpleInflationProtection ? 'simple' : 'compound'}</b><br/>
          Coverage period: <b>${boldThousands(baseAmountApplicableDays)}</b> days<br/>
          Inflated daily benefit: ${boldCurrency(cappedDailyBenefitAmountBase)}<br/>`;
      const maxCoverageText = `
        Maximum coverage based on daily max: ${boldCurrency(inflatedDailyMaxFace)}<br/>
        <i>(${boldCurrency(cappedDailyBenefitAmountBase)} per day × ${boldThousands(baseAmountApplicableDays)} days)</i><br/>`;

      dailyMaximumBenefitBody +=
        cappedDailyBenefitAmountBase === phaseCostPerDayNeeded
          ? cappedExplainerText
          : inflationText;

      dailyMaximumBenefitBody += maxCoverageText;
    }

    if (riderAmountApplicableDays && riderAmountApplicableDays > 0) {
      dailyMaximumBenefitBody += `
        <u>Daily max during COB rider:</u><br/>`;

      const inflationText = `
          Inflation rate: <b>${formatPercent(policyInflationProtectionOnCOBAmount ?? inflationProtection)} ${isSimpleInflationProtection ? 'simple' : 'compound'}</b><br/>
          Coverage period: <b>${boldThousands(riderAmountApplicableDays)}</b> days<br/>
          Inflated daily benefit: ${boldCurrency(cappedDailyBenefitAmountRider)}<br/>`;
      const maxCoverageText = `
        Maximum coverage based on daily max: ${boldCurrency(inflatedDailyMaxCOB)}<br/>
        <i>(${boldCurrency(cappedDailyBenefitAmountRider)} per day × ${boldThousands(riderAmountApplicableDays)} days)</i><br/>`;

      dailyMaximumBenefitBody +=
        cappedDailyBenefitAmountRider === phaseCostPerDayNeeded
          ? cappedExplainerText
          : inflationText;

      dailyMaximumBenefitBody += maxCoverageText;
    }

    dailyMaximumBenefitBody += `
      Total maxmimum coverage based on daily max: ${boldUnderlineCurrency(inflatedDailyMaxFace + inflatedDailyMaxCOB)}<br/>
    `;
  } else {
    if (dailyMaxExists) {
      dailyMaximumBenefitBody += `Daily max benefit for care ${careLocationDescription}: ${boldCurrency(dailyMaximumBenefitForCareEnvironment)}.</br>
    `;
      if (inflationProtection) {
        dailyMaximumBenefitBody += `${inflationProtectionText}
      Inflated daily max benefit: ${inflatedDailyBenefitAmount >= phaseCostPerDayNeeded && !isIndemnityPolicyPayment ? boldCurrency(inflatedDailyBenefitAmount) : boldUnderlineCurrency(inflatedDailyBenefitAmount)}.<br/> ${inflatedDailyMaxText}`;
      }
      if (!isIndemnityPolicyPayment) {
        // reimbursement policy

        const expectedDailyPayout =
          inflatedDailyBenefitAmount >= phaseCostPerDayNeeded
            ? phaseCostPerDayNeeded
            : inflatedDailyBenefitAmount;

        if (inflatedDailyBenefitAmount >= phaseCostPerDayNeeded) {
          dailyMaximumBenefitBody += `Expected daily payout: ${boldUnderlineCurrency(expectedDailyPayout)}. `;
          dailyMaximumBenefitBody += `<i>The daily max is more than the expected average daily cost of care (${boldCurrency(phaseCostPerDayNeeded)}) for ${clientFirstName}, so a <b>reimbursement</b> policy will only pay up to the actual cost incurred.</i>
        `;
        }
      } else {
        // indemnity policy
        dailyMaximumBenefitBody += `<i>This is an <b>indemnity</b> policy, which means that the policy will pay the full ${boldCurrency(inflatedDailyBenefitAmount)} per day regardless of the actual cost of care. Any excess funds will be used in future care phases or added to the <b>Non-LTC Payout</b> amount.</i>`;
      }
    }
  }
  return (
    <>
      {dailyMaximumBenefitForCareEnvironment || dailyMaximumBenefitBody.length > 0 ? (
        <>
          <h4 className="text-md flex flex-grow flex-col gap-3 pb-2 font-semibold">
            {dailyMaximumBenefitSubtitle}
          </h4>
          <p
            className="pb-1"
            dangerouslySetInnerHTML={{
              __html: dailyMaximumBenefitBody,
            }}
          ></p>
        </>
      ) : null}
    </>
  );
}

function PolicyEligibleCareDays({
  client,
  phaseCosts,
  phaseCalculations,
}: ClientPhaseProps): JSX.Element | null {
  const {
    clientFirstName,
    intakeSurvey: { clientAdlReceivedAssistanceCount, clientCurrentCarePhase },
    appliedCareEnvironments,
  } = client;
  const {
    ltcPolicy,
    ltcPolicy: { policyType },
  } = useWhiteBoxContext();

  const { homeCareWaitingPeriodDays, facilityCareWaitingPeriodDays } = ltcPolicy;
  const carePhase = phaseCosts.carePhase;
  const carePhaseIndex = carePhaseDefs[carePhase].index;
  const isCurrentPhase = clientCurrentCarePhase === carePhase;

  const phaseCareDaysNeeded = phaseCalculations.phaseCareDaysNeeded;
  const halfPhaseCareDaysNeeded = phaseCalculations.daysWhereLessThanTwoADLs;
  const selectedCareSetting = appliedCareEnvironments?.[carePhase];
  const waitingPeriodDaysForCareEnvironment =
    selectedCareSetting === CareEnvironment.home
      ? homeCareWaitingPeriodDays
      : facilityCareWaitingPeriodDays;
  const applicableWaitingPeriodDays = phaseCalculations.applicableWaitingPeriodDays;
  const applicablePhaseCareDaysNeeded = halfPhaseCareDaysNeeded
    ? halfPhaseCareDaysNeeded
    : phaseCareDaysNeeded;
  const applicablePhaseCareDaysNeededAfterWaitingPeriod =
    phaseCalculations.phaseEligibleCareDaysAfterWaitingPeriod;

  const phaseHomeWaitingPeriodDaysRemaining =
    phaseCalculations.phaseHomeCareWaitingPeriodDaysRemaining;

  const phaseFacilityWaitingPeriodDaysRemaining =
    phaseCalculations.phaseFacilityCareWaitingPeriodDaysRemaining;

  const phaseWaitingPeriodDaysRemaining =
    selectedCareSetting === CareEnvironment.home
      ? phaseHomeWaitingPeriodDaysRemaining
      : phaseFacilityWaitingPeriodDaysRemaining;

  let nonEligibleDaysPayoutSubtitle = `Eligible care days`;
  let nonEligibleDaysPayoutBody = ``;

  const isEarlyCareWithLessThen2Adls =
    carePhaseIndex === 0 &&
    clientAdlReceivedAssistanceCount < 2 &&
    policyType !== FundingPolicyType.fortifiedLife;
  const hasWaitingPeriodDaysForCareEnvironment = !isNullOrUndefined(
    waitingPeriodDaysForCareEnvironment,
  );

  let earlyCare2AdlEligibilitySubtitle = `2 ADL claim eligibility requirement`;
  let earlyCare2AdlEligibilityBody = `
  Estimated ${isCurrentPhase ? 'remaining ' : ''}days in Early Care Phase: <b>${phaseCareDaysNeeded} days</b>.<br/>
  Eligible days associated with 2-ADLs: ${hasWaitingPeriodDaysForCareEnvironment ? boldThousands(halfPhaseCareDaysNeeded) : boldUnderlineInteger(halfPhaseCareDaysNeeded)} <b>days</b>.<br/>
  <i>Most long-term care insurance policies <b>won't be claim-eligible</b> until the policyholder is receiving help with at least <b>2 Activities of Daily Living (ADLs)</b>.
  We refer to these ADLs as self-care activities earlier in the Personalize Plan section.

    <br/>Early Care Phase is a prediction for when ${clientFirstName} will need help with 1-2 ADLs.
    We assume the first half of ${isCurrentPhase ? 'the remainder of ' : ''} this care phase is spent receiving help with 1 ADL (and is not covered by the policy).</i>`;

  let waitingPeriodDaysSubtitle = `Waiting period`;
  let waitingPeriodDaysBody = '';

  waitingPeriodDaysBody += `Remaining ${selectedCareSetting === CareEnvironment.home ? 'home care' : 'facility'} waiting period: <b>${phaseWaitingPeriodDaysRemaining} (out of ${selectedCareSetting === CareEnvironment.home ? homeCareWaitingPeriodDays : facilityCareWaitingPeriodDays}) days</b>.<br/>`;
  waitingPeriodDaysBody += `Eligible days after waiting period: <u><b>${applicablePhaseCareDaysNeededAfterWaitingPeriod} days</b></u>.
  <i>This is calculated by subtracting eligible days in the care phase by the remaining waiting period days (${applicablePhaseCareDaysNeeded} days - ${applicableWaitingPeriodDays} days).</i><br/>`;

  if (isEarlyCareWithLessThen2Adls || hasWaitingPeriodDaysForCareEnvironment) {
    return (
      <>
        <h4 className="mt-1 flex flex-grow flex-col gap-3 pb-2 text-base font-semibold">
          {nonEligibleDaysPayoutSubtitle}
        </h4>
        <p
          className="pb-1"
          dangerouslySetInnerHTML={{ __html: nonEligibleDaysPayoutBody }}
        ></p>
        {isEarlyCareWithLessThen2Adls ? (
          <div className="">
            <h4 className="flex flex-grow flex-col gap-3 pb-2 text-sm font-semibold">
              {earlyCare2AdlEligibilitySubtitle}
            </h4>
            <p
              className="pb-2"
              dangerouslySetInnerHTML={{
                __html: earlyCare2AdlEligibilityBody,
              }}
            ></p>
          </div>
        ) : null}
        {hasWaitingPeriodDaysForCareEnvironment && (
          <div>
            <h4 className="flex flex-grow flex-col gap-3 pb-2 text-sm font-semibold">
              {waitingPeriodDaysSubtitle}
            </h4>
            <p
              className="pb-1"
              dangerouslySetInnerHTML={{ __html: waitingPeriodDaysBody }}
            ></p>
          </div>
        )}
        <PolicyEpReimbursement
          client={client}
          phaseCosts={phaseCosts}
          phaseCalculations={phaseCalculations}
        />
      </>
    );
  }
  return null;
}

function PolicyEpReimbursement({ phaseCalculations }: ClientPhaseProps): JSX.Element | null {
  const { ltcPolicy } = useWhiteBoxContext();

  const epReimbursementAmount = phaseCalculations.epReimbursementAmount ?? 0;
  const epDaysReimbursed = phaseCalculations.epDaysReimbursed ?? 0;
  if (epReimbursementAmount <= 0) {
    return null;
  }

  const inflatedDailyBenefit = phaseCalculations.inflatedDailyBenefitAmount ?? 0;
  const phaseCostPerDay = phaseCalculations.phaseCostPerDayNeeded ?? 0;
  const effectivePerDay = epDaysReimbursed > 0 ? epReimbursementAmount / epDaysReimbursed : 0;
  const isIndemnity = ltcPolicy.isIndemnityPolicyPayment;
  const wasCostClamped =
    !isIndemnity && phaseCostPerDay > 0 && phaseCostPerDay < inflatedDailyBenefit;
  const uncappedPerDay = isIndemnity
    ? inflatedDailyBenefit
    : Math.min(inflatedDailyBenefit, phaseCostPerDay);
  const wasPoolClamped = effectivePerDay > 0 && effectivePerDay < uncappedPerDay - 0.01;

  let body = `Waiting period satisfied this phase. The policy pays the ${boldInteger(
    epDaysReimbursed,
  )} served waiting period days as a retroactive lump sum: ${boldInteger(
    epDaysReimbursed,
  )} days × ${boldCurrency(effectivePerDay)} = ${boldUnderlineCurrency(epReimbursementAmount)}.<br/>`;
  if (isIndemnity) {
    body += `<i>Because this is an indemnity policy, the lump is paid at the full daily benefit of ${boldCurrency(
      inflatedDailyBenefit,
    )}.</i><br/>`;
  } else if (wasCostClamped) {
    body += `<i>Because this is a reimbursement policy, the per-day rate is capped at the phase's cost-per-day of ${boldCurrency(
      phaseCostPerDay,
    )} (below the daily benefit of ${boldCurrency(
      inflatedDailyBenefit,
    )}) — reimbursement can't exceed actual incurred cost.</i><br/>`;
  } else {
    body += `<i>Paid at the daily benefit of ${boldCurrency(inflatedDailyBenefit)}.</i><br/>`;
  }
  if (wasPoolClamped) {
    body += `<i>The lump was further reduced because the remaining benefit pool wasn't large enough to cover it in full.</i><br/>`;
  }

  return (
    <div>
      <h4 className="flex flex-grow flex-col gap-3 pb-2 text-sm font-semibold">
        Retroactive waiting period reimbursement
      </h4>
      <p
        className="pb-1"
        dangerouslySetInnerHTML={{ __html: body }}
      ></p>
    </div>
  );
}

function PhaseCostBreakdown({
  client,
  phaseCosts,
  phaseCalculations,
  isJointPolicy,
}: ClientPhaseProps): JSX.Element | null {
  const {
    clientFullName,
    clientFirstName,
    intakeSurvey: { clientCurrentCarePhase },
    intakeSurvey,
    appliedCareEnvironments,
  } = client;

  const carePhase = phaseCosts.carePhase;
  const carePhaseLabel = carePhaseDefs[carePhase].label;
  const carePhaseIndex = carePhaseDefs[carePhase].index;

  const phaseCostBreakDownSubtitle = isJointPolicy
    ? `${clientFirstName}'s Payout for the ${carePhaseLabel} Phase`
    : `${carePhaseIndex + 1}. Payout for the ${carePhaseLabel} Phase`;

  const isCurrentPhase = clientCurrentCarePhase === carePhase;
  const isPastPhase = clientCurrentCarePhase
    ? carePhaseDefs[clientCurrentCarePhase].index > carePhaseDefs[carePhase].index
    : null;

  if (isPastPhase) {
    return null;
  }

  const inflatedProfessionalPhaseCosts = phaseCosts.phaseInflatedProfessionalShareCost;
  const phaseCareMonthsNeeded = phaseCosts.phaseCareMonthsNeeded;
  const phaseMonthlyCost = phaseCalculations.phaseMonthlyCost;
  const phaseCostPerDayNeeded = phaseCalculations.phaseCostPerDayNeeded;

  // early care
  const careLocation = appliedCareEnvironments?.[carePhase] as CareEnvironment;

  const phaseCostBreakDownBody = `${clientFullName} is planning to live <b>${careEnvironmentDefs[careLocation].facilityType}</b> for ${isCurrentPhase ? 'the remainder of ' : ''}${possessivePronoun(intakeSurvey)} ${carePhaseLabel} Phase. Below are some predicted details about this phase:<br/>
   Total phase cost: ${boldCurrency(inflatedProfessionalPhaseCosts)}.<br/>
   Phase duration: <b>${phaseCareMonthsNeeded} months (${formatInteger(phaseCareMonthsNeeded * 30)} days) </b>.<br/>
   Average monthly cost: ${boldCurrency(phaseMonthlyCost)}.<br/>
   Average daily cost: ${boldCurrency(phaseCostPerDayNeeded)}.<br/>`;
  return (
    <>
      <h3 className="-ml-2 flex flex-grow flex-col gap-3 pb-2 pt-3 text-lg font-semibold">
        {phaseCostBreakDownSubtitle}
      </h3>
      <p
        className="pb-1"
        dangerouslySetInnerHTML={{ __html: phaseCostBreakDownBody }}
      ></p>
    </>
  );
}

function formatCurrencyTwoDecimals(value: number | undefined | null): string {
  return formatCurrency(value, 2);
}
